import log from '@/lib/logger';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserGroupIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { Text } from '@/components/tailwind/text';
import { useAuth } from '@/context/use-auth';
import { getDashboardMemberStatistics, DashboardMemberStatistics } from '@/services/dashboard-services';

export function MemberStatisticsWidget() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation('common');
    const [statistics, setStatistics] = useState<DashboardMemberStatistics | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        async function loadStatistics() {
            if (!token) return;

            setIsLoading(true);
            setError(null);
            try {
                const stats = await getDashboardMemberStatistics(token);
                setStatistics(stats);
            } catch (err) {
                log.error('Failed to load member statistics:', err);
                setError(t('dashboard.widgets.memberStatistics.error'));
            } finally {
                setIsLoading(false);
            }
        }

        loadStatistics();
    }, [token, t]);

    function handleClick() {
        navigate('/members');
    }

    if (isLoading) {
        return (
            <div 
                className="rounded-lg bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800 p-6" 
                data-testid="member-statistics-widget"
            >
                <div className="animate-pulse">
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-3"></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div 
                className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 p-6" 
                data-testid="member-statistics-widget"
            >
                <div className="text-red-800 dark:text-red-200 text-sm">{error}</div>
            </div>
        );
    }

    return (
        <div
            className="rounded-lg bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800 p-6 cursor-pointer hover:shadow-md transition-shadow"
            onClick={handleClick}
            data-testid="member-statistics-widget"
        >
            <div className="flex items-center space-x-2 mb-3">
                <UserGroupIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {t('dashboard.widgets.memberStatistics.title')}
                </h3>
            </div>

            {/* Total count */}
            <div className="flex items-baseline space-x-2 mb-4">
                <span
                    className="text-3xl font-bold text-gray-900 dark:text-gray-100"
                    data-testid="total-members-count"
                >
                    {statistics?.totalCount ?? 0}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                    {t('dashboard.widgets.memberStatistics.total')}
                </span>
            </div>

            {/* Active / Passive breakdown */}
            <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex flex-col">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                        {t('dashboard.widgets.memberStatistics.active')}
                    </span>
                    <span 
                        className="text-xl font-semibold text-green-600 dark:text-green-400"
                        data-testid="active-members-count"
                    >
                        {statistics?.activeCount ?? 0}
                    </span>
                </div>
                <div className="flex flex-col">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                        {t('dashboard.widgets.memberStatistics.passive')}
                    </span>
                    <span 
                        className="text-xl font-semibold text-gray-600 dark:text-gray-400"
                        data-testid="passive-members-count"
                    >
                        {statistics?.passiveCount ?? 0}
                    </span>
                </div>
            </div>

            {/* Yearly changes */}
            <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-3">
                <div className="flex items-center space-x-1">
                    <ArrowUpIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium text-green-600 dark:text-green-400" data-testid="new-members-count">
                            {statistics?.newMembersThisYear ?? 0}
                        </span>
                        {' '}{t('dashboard.widgets.memberStatistics.joinedThisYear')}
                    </span>
                </div>
                <div className="flex items-center space-x-1">
                    <ArrowDownIcon className="h-4 w-4 text-red-600 dark:text-red-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium text-red-600 dark:text-red-400" data-testid="cancelled-members-count">
                            {statistics?.cancelledMembersThisYear ?? 0}
                        </span>
                        {' '}{t('dashboard.widgets.memberStatistics.leftThisYear')}
                    </span>
                </div>
            </div>

            <Text className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                {t('dashboard.widgets.memberStatistics.clickForDetails')}
            </Text>
        </div>
    );
}
